import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Loader2, RefreshCw, ShieldCheck, AlertTriangle, ChefHat, Truck, ArrowRight, CalendarCheck,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import {
  addDays, fmtDate, hoursLabel, levelStyle, money, todayStr, weekStartOf,
} from '../../../lib/scheduleFormat';
import { IncidentLog } from './IncidentLog';

const RANGES = [
  { label: 'Next 2 weeks', days: 14 },
  { label: 'Next 4 weeks', days: 28 },
  { label: 'Next 8 weeks', days: 56 },
];

const Coverage = () => {
  const [searchParams] = useSearchParams();
  const [rangeDays, setRangeDays] = useState(28);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rechecking, setRechecking] = useState(false);
  const highlight = searchParams.get('date');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = todayStr();
      const data = await adminService.getCoverageBoard(start, addDays(start, rangeDays));
      setBoard(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the coverage board');
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);

  const recheck = async () => {
    setRechecking(true);
    try {
      await adminService.evaluateCoverage({ days_ahead: rangeDays });
      toast.success('Coverage re-checked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not re-check coverage');
    } finally {
      setRechecking(false);
    }
  };

  const counts = board?.counts || { red: 0, amber: 0, green: 0 };
  const rows = board?.rows || [];

  return (
    <div className="space-y-5" data-testid="coverage-board">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Coverage</h1>
          <p className="mt-1 text-sm text-gray-600">
            Every delivery date carrying live orders, checked for kitchen hours, a baker and a driver.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={recheck} disabled={rechecking} data-testid="coverage-recheck">
            {rechecking ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
            Re-check all
          </Button>
          <Link to="/admin/scheduling/builder">
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="coverage-open-builder">
              Open the builder <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((range) => (
          <Button
            key={range.days}
            variant={rangeDays === range.days ? 'default' : 'outline'}
            size="sm"
            className={rangeDays === range.days ? 'bg-gray-900 hover:bg-gray-800' : ''}
            onClick={() => setRangeDays(range.days)}
            data-testid={`coverage-range-${range.days}`}
          >
            {range.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-testid="coverage-summary">
        {[
          { key: 'red', label: 'Not covered', icon: AlertTriangle },
          { key: 'amber', label: 'Needs attention', icon: CalendarCheck },
          { key: 'green', label: 'Covered', icon: ShieldCheck },
        ].map(({ key, label, icon: Icon }) => {
          const style = levelStyle(key);
          return (
            <Card key={key} className={style.card} data-testid={`coverage-count-${key}`}>
              <CardContent className="flex items-center gap-3 p-4">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full ${style.dot} bg-opacity-15`}>
                  <Icon className="h-5 w-5 text-gray-800" />
                </span>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts[key] || 0}</p>
                  <p className="text-sm text-gray-600">{label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <IncidentLog />

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center" data-testid="coverage-loading">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center" data-testid="coverage-empty">
            <p className="font-medium text-gray-900">No delivery dates carry orders in this window.</p>
            <p className="mt-1 text-sm text-gray-600">
              Coverage only flags dates with live orders, so there is nothing to staff yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="coverage-rows">
          {rows.map((row) => {
            const style = levelStyle(row.level);
            return (
              <Card
                key={row.date}
                className={`${style.card} ${highlight === row.date ? 'ring-2 ring-amber-400' : ''}`}
                data-testid={`coverage-row-${row.date}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-gray-900">{fmtDate(row.date)}</p>
                      <p className="text-xs text-gray-600">
                        {row.order_count} order{row.order_count === 1 ? '' : 's'} · {row.units} units
                        {row.recurring_pending > 0 && ` · ${row.recurring_pending} recurring pending`}
                      </p>
                    </div>
                    <Badge variant="outline" className={style.badge} data-testid={`coverage-level-${row.date}`}>
                      {style.label}
                    </Badge>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-700">
                    <span className="rounded-full bg-white px-2 py-1 ring-1 ring-gray-200">
                      {row.bookings.length} booking{row.bookings.length === 1 ? '' : 's'}
                      {row.bookings.length > 0 && ` · ${hoursLabel(row.hours.booked)}`}
                    </span>
                    <span className="rounded-full bg-white px-2 py-1 ring-1 ring-gray-200">
                      <ChefHat className="mr-1 inline h-3 w-3" />{row.bakers} baker{row.bakers === 1 ? '' : 's'}
                    </span>
                    <span className="rounded-full bg-white px-2 py-1 ring-1 ring-gray-200">
                      <Truck className="mr-1 inline h-3 w-3" />{row.drivers} driver{row.drivers === 1 ? '' : 's'}
                    </span>
                    {row.hours.required !== null && row.hours.required !== undefined && (
                      <span
                        className={`rounded-full px-2 py-1 ring-1 ${
                          row.hours.covered === false
                            ? 'bg-amber-50 text-amber-900 ring-amber-300'
                            : 'bg-white ring-gray-200'
                        }`}
                        title="Person-hours of work the volume implies, against the person-hours actually scheduled"
                        data-testid={`coverage-workload-${row.date}`}
                      >
                        ≈{hoursLabel(row.hours.required)} of work
                        {' · '}
                        {hoursLabel(row.hours.staffed)} scheduled
                      </span>
                    )}
                    {row.bookings.length > 0 && (
                      <span className="rounded-full bg-white px-2 py-1 ring-1 ring-gray-200">
                        {money(row.bookings.reduce((sum, b) => sum + (b.cost || 0), 0))}
                      </span>
                    )}
                  </div>

                  {row.reasons.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid={`coverage-reasons-${row.date}`}>
                      {row.reasons.map((reason) => (
                        <li key={reason.code} className="flex items-start gap-2 text-xs text-gray-800">
                          <span
                            className={`mt-1 h-2 w-2 shrink-0 rounded-full ${levelStyle(reason.severity).dot}`}
                          />
                          <span>{reason.message}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {!row.hours.standards_configured && (
                    <p className="mt-3 text-[11px] text-gray-500">
                      Labour standards are not set, so this runs on presence checks only.
                    </p>
                  )}

                  <Link
                    to={`/admin/scheduling/builder?week=${weekStartOf(row.date)}`}
                    className="mt-3 inline-flex items-center text-sm font-medium text-amber-700 hover:text-amber-800"
                    data-testid={`coverage-open-${row.date}`}
                  >
                    Open this day in the builder <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Coverage;
